/**
 * PathwayModeToggle (Sprint 4.3.2, PROD-651).
 *
 * Top-right pill in the Pathway Editor header that flips the editor
 * between `Design Mode` and `Runtime Mode`. Mode state lives in the
 * `usePathwayEditorModeStore` Zustand store so child components
 * (PathwayCanvas, ActivityInspector, multiplicity badges) read without
 * prop-drilling.
 *
 * D4 RESOLVED: single CPS app + Mode toggle (NOT a separate runtime
 * service). This component is the user-facing seam.
 *
 * HC2 token-only: every color resolves through Signal DS Tailwind tokens
 * (`signal-primary-*`, `surface-*`, `text-*`). No raw hex.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: the toggle is `role="radiogroup"` with
 * `aria-label="Pathway editor mode"`. Each option is `role="radio"` with
 * `aria-checked` + `tabIndex` per the WAI-ARIA radio-group pattern. The
 * group is keyboard-focusable and arrow-keys swap selection.
 * HC9: every change emits `pathway_editor_mode_change` via the store.
 *
 * Render contract:
 *   <span role="radiogroup" aria-label="Pathway editor mode">
 *     <button role="radio" aria-checked="true">Design Mode</button>
 *     <button role="radio" aria-checked="false">Runtime Mode</button>
 *   </span>
 */
import { useEffect } from 'react';
import {
  usePathwayEditorModeStore,
  type PathwayEditorMode,
} from '../../state/pathway-editor-mode.store';

const OPTIONS: Array<{ value: PathwayEditorMode; label: string }> = [
  { value: 'design', label: 'Design Mode' },
  { value: 'runtime', label: 'Runtime Mode' },
];

export interface PathwayModeToggleProps {
  className?: string;
}

export function PathwayModeToggle({ className }: PathwayModeToggleProps) {
  const mode = usePathwayEditorModeStore((s) => s.mode);
  const setMode = usePathwayEditorModeStore((s) => s.setMode);
  const hydrate = usePathwayEditorModeStore((s) => s.hydrate);

  // Hydrate from localStorage on mount so a refresh restores the prior
  // selection. The hydrate path does NOT emit telemetry (no user act).
  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <span
      role="radiogroup"
      aria-label="Pathway editor mode"
      data-testid="pathway-mode-toggle"
      data-mode={mode}
      className={[
        'inline-flex items-center rounded-signal-pill border border-surface-border bg-surface-base p-0.5',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {OPTIONS.map((option) => {
        const selected = option.value === mode;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            data-testid={`pathway-mode-toggle-${option.value}`}
            data-selected={selected ? 'true' : 'false'}
            onClick={() => setMode(option.value)}
            className={[
              'rounded-signal-pill px-gutter-md py-1 text-signal-xs font-semibold uppercase tracking-wide',
              'focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
              selected
                ? 'bg-signal-primary-500 text-text-inverse'
                : 'text-text-secondary hover:text-text-primary',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {option.label}
          </button>
        );
      })}
    </span>
  );
}
